/**
 * User Profile page - Personal data, settings, premium management, reservations, help
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Mail, Phone, Edit2, Save, X, LogOut, 
  Crown, Check, Star, Zap, Shield, CreditCard, Calendar,
  ChevronRight, ChevronDown, Settings, Bell, HelpCircle, Radio, AlertTriangle, Trash2, ShoppingCart, Lock, Key, Send, Loader2,
  MessageSquarePlus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Navbar } from '../components/Navbar';
import { AccountStatusPanel } from '../components/AccountStatusPanel';
import { useAuthStore, useSessionStore } from '../store';
import { authAPI, userShellyAPI, faqAPI } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';

// Premium plan - Coming Soon with real features
const PREMIUM_FEATURES = [
  { icon: '🎬', title: 'Videos 60s a 2x', description: 'Replays de 60 segundos a velocidad doble' },
  { icon: '🎥', title: 'Videos 90s a 3x', description: 'Replays de 90 segundos a velocidad triple' },
  { icon: '⌚', title: 'Smartwatch', description: 'Apple Watch, Android Wear OS 3.5 y Garmin' },
  { icon: '🔘', title: 'Shelly Premium', description: 'Botón Shelly con videos en 2x y 3x' },
  { icon: '💾', title: '7 días de almacenaje', description: 'Tus replays se guardan por más tiempo' },
];

// Plans with "Coming Soon"
const PLANS = [
  {
    id: 'free',
    name: 'Gratis',
    price: 0,
    features: [
      'Videos de 30 segundos',
      'Velocidad normal (1x)',
      'Retención 48 horas',
      'Compartir con marca de agua',
      '⭐ Algunas funciones premium liberadas temporalmente',
    ],
    current: true,
    note: 'Algunas características premium están disponibles por tiempo limitado',
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 149,
    period: '/mes',
    features: [
      'Videos de 60s a 2x velocidad',
      'Videos de 90s a 3x velocidad',
      'Integración Smartwatch (Apple, Android, Garmin)',
      'Botón Shelly con videos 2x y 3x',
      '7 días de almacenaje de replays',
      'Sin marca de agua',
      'Soporte prioritario',
    ],
    popular: true,
    comingSoon: true,
  },
];

export default function Profile() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout: authLogout, setUser } = useAuthStore();
  const { clearSession } = useSessionStore();
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Shelly buttons state
  const [shellyButtons, setShellyButtons] = useState([]);
  const [loadingButtons, setLoadingButtons] = useState(true);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [selectedButton, setSelectedButton] = useState(null);
  const [deactivationReason, setDeactivationReason] = useState('Botón perdido');
  
  // Phone change state
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);
  const [phoneStep, setPhoneStep] = useState(1); // 1: enter phone, 2: enter code
  const [newPhone, setNewPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  
  // Settings state
  const [settings, setSettings] = useState({
    notifications: true,
    marketing: false,
    autoDownload: false,
  });
  
  // Reservations state
  const [reservations, setReservations] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(true);
  
  // FAQ state
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [showHelpSection, setShowHelpSection] = useState(false);
  const [faqCategories, setFaqCategories] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [loadingFAQ, setLoadingFAQ] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // User question state
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [questionCategory, setQuestionCategory] = useState('otros');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [myQuestions, setMyQuestions] = useState([]);
  const [showMyQuestions, setShowMyQuestions] = useState(false);
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordChanging, setPasswordChanging] = useState(false);
  // Email verification state
  const [emailOtp, setEmailOtp] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);

  
  // Fetch Shelly buttons
  const fetchShellyButtons = useCallback(async () => {
    try {
      setLoadingButtons(true);
      const res = await userShellyAPI.getMyButtons();
      setShellyButtons(res.data.buttons || []);
    } catch (err) {
      console.error('Error fetching Shelly buttons:', err);
    } finally {
      setLoadingButtons(false);
    }
  }, []);
  
  // Fetch user reservations
  const fetchReservations = useCallback(async () => {
    try {
      setLoadingReservations(true);
      const res = await api.get('/reservations/my-reservations');
      setReservations(res.data.reservations || []);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setReservations([]);
    } finally {
      setLoadingReservations(false);
    }
  }, []);
  
  // Fetch FAQ data
  const fetchFAQData = useCallback(async () => {
    try {
      setLoadingFAQ(true);
      const [categoriesRes, itemsRes] = await Promise.all([
        faqAPI.getCategories(),
        faqAPI.getItems()
      ]);
      setFaqCategories(categoriesRes.data.categories || []);
      setFaqItems(itemsRes.data.items || []);
    } catch (err) {
      console.error('Error fetching FAQ:', err);
    } finally {
      setLoadingFAQ(false);
    }
  }, []);
  
  // Fetch my questions
  const fetchMyQuestions = useCallback(async () => {
    try {
      const res = await faqAPI.getMyQuestions();
      setMyQuestions(res.data.questions || []);
    } catch (err) {
      console.error('Error fetching my questions:', err);
    }
  }, []);
  
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
      fetchShellyButtons();
      fetchReservations();
      fetchFAQData();
      fetchMyQuestions();
    }
  }, [user, fetchShellyButtons, fetchReservations, fetchFAQData, fetchMyQuestions]);
  
  // Submit user question
  const handleSubmitQuestion = async () => {
    if (!newQuestion.trim()) {
      toast.error('Por favor escribe tu pregunta');
      return;
    }
    
    setSubmittingQuestion(true);
    try {
      await faqAPI.submitQuestion({
        question: newQuestion,
        category_slug: questionCategory
      });
      toast.success('¡Pregunta enviada! Te responderemos pronto.');
      setShowQuestionDialog(false);
      setNewQuestion('');
      setQuestionCategory('otros');
      fetchMyQuestions();
    } catch (err) {
      console.error('Error submitting question:', err);
      toast.error('Error al enviar pregunta');
    } finally {
      setSubmittingQuestion(false);
    }
  };
  
  // Get FAQ items grouped by category
  const getFAQByCategory = (categorySlug) => {
    return faqItems.filter(item => item.category_slug === categorySlug);
  };
  
  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    if (passwordForm.new.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    
    setPasswordChanging(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      });
      toast.success('Contraseña actualizada correctamente');
      setShowPasswordDialog(false);
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (err) {
      console.error('Password change error:', err);
      toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleSendEmailOTP = async () => {
    if (!formData.email?.trim()) {
      toast.error('Ingresa un correo para verificar');
      return;
    }

    setEmailSending(true);
    try {
      await api.post('/auth/email/send-magic-link', { email: formData.email.trim().toLowerCase() });
      toast.success('Link de verificación enviado a tu correo. Haz clic en él para verificar.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg || 'No se pudo enviar el link');
      } else {
        toast.error('No se pudo enviar el link');
      }
    } finally {
      setEmailSending(false);
    }
  };

  // Legacy function kept for compatibility but not used with Magic Link flow
  const handleVerifyEmailOTP = async () => {
    toast.info('Con Magic Link, solo debes hacer clic en el enlace que enviamos a tu correo');
  };
  
  const handleRequestDeactivation = async () => {
    if (!selectedButton) return;
    
    try {
      await userShellyAPI.requestDeactivation(selectedButton.device_id, deactivationReason);
      toast.success('Solicitud de baja enviada');
      setShowDeactivateDialog(false);
      setSelectedButton(null);
      setDeactivationReason('Botón perdido');
      fetchShellyButtons();
    } catch (err) {
      console.error('Deactivation request error:', err);
      toast.error(err.response?.data?.detail || 'Error enviando solicitud');
    }
  };
  
  const handleSave = async () => {
    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();

    if (!firstName || !lastName) {
      toast.error('Nombre y apellido son obligatorios');
      return;
    }

    setLoading(true);
    try {
      const profileRes = await authAPI.updateProfile({
        first_name: firstName,
        last_name: lastName,
      });

      const updatedUser = profileRes.data?.user;
      if (updatedUser) {
        setUser(updatedUser);
      }

      // If email changed, mark as unverified locally and require OTP verification
      if (formData.email?.trim().toLowerCase() !== (user?.email || '').toLowerCase()) {
        setUser({
          ...(updatedUser || user),
          email: formData.email.trim().toLowerCase(),
          email_verified: false,
        });
        toast.info('Guarda tu nuevo correo y luego verifícalo con OTP para habilitar compartir/descargar');
      } else {
        toast.success('Perfil actualizado');
      }

      setIsEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
    
    clearSession();
    authLogout();
    toast.success('Sesión cerrada');
    navigate('/');
  };
  
  // Phone change handlers
  const handleOpenPhoneDialog = () => {
    setNewPhone(user?.phone || '');
    setPhoneCode('');
    setPhoneStep(1);
    setShowPhoneDialog(true);
  };
  
  const handleSendPhoneOTP = async () => {
    // Validate phone format
    let phoneToSend = newPhone.trim();
    if (!phoneToSend.startsWith('+')) {
      // If no country code, assume Mexico
      phoneToSend = '+52' + phoneToSend.replace(/\D/g, '');
    }
    
    if (phoneToSend.length < 12) {
      toast.error('Ingresa un número de teléfono válido con código de país');
      return;
    }
    
    setPhoneSending(true);
    try {
      await authAPI.sendPhoneOTP(phoneToSend);
      setNewPhone(phoneToSend);
      setPhoneStep(2);
      toast.success('Código enviado por SMS');
    } catch (err) {
      console.error('Send OTP error:', err);
      toast.error(err.response?.data?.detail || 'Error al enviar código');
    } finally {
      setPhoneSending(false);
    }
  };
  
  const handleVerifyAndChangePhone = async () => {
    if (!phoneCode || phoneCode.length < 4) {
      toast.error('Ingresa el código de verificación');
      return;
    }
    
    setPhoneVerifying(true);
    try {
      const response = await authAPI.changePhone(newPhone, phoneCode);
      
      // Update user in store
      if (response.data.user) {
        setUser(response.data.user);
        setFormData(prev => ({ ...prev, phone: response.data.user.phone }));
      }
      
      toast.success('Número de teléfono actualizado');
      setShowPhoneDialog(false);
      setPhoneStep(1);
      setPhoneCode('');
    } catch (err) {
      console.error('Verify phone error:', err);
      toast.error(err.response?.data?.detail || 'Código inválido o expirado');
    } finally {
      setPhoneVerifying(false);
    }
  };
  
  const handleSelectPlan = (plan) => {
    if (plan.id === 'free') {
      setShowPremiumDialog(false);
      return;
    }
    setSelectedPlan(plan);
    // TODO: Integrate with payment gateway (Stripe)
    toast.info('Próximamente: Integración de pagos');
  };
  
  const currentPlan = PLANS.find(p => p.current) || PLANS[0];
  
  return (
    <div className="min-h-screen bg-[#070A0F]">
      <Navbar />
      
        <AccountStatusPanel
          user={user}
          onCompleteProfile={() => setIsEditing(true)}
          onVerifyEmail={() => {
            setIsEditing(true);
            handleSendEmailOTP();
          }}
          onOpenSecurity={() => setShowPasswordDialog(true)}
        />

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Profile Header - Gold Standard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#0D121D] rounded-2xl p-6 border border-white/10"
        >
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#BBFF00]/10 flex items-center justify-center border border-[#BBFF00]/30">
                <User className="w-8 h-8 text-[#BBFF00]" />
              </div>
              <div>
                <h2 className="font-display font-bold text-xl text-slate-50">
                  {user?.first_name || 'Usuario'} {user?.last_name || ''}
                </h2>
                <p className="text-sm text-slate-400">{user?.email || 'Sin email'}</p>
                {/* User ID - visible for integration purposes */}
                <p className="text-xs text-slate-600 font-mono mt-0.5">
                  ID: {user?.id || 'N/A'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#BBFF00]/10 text-[#BBFF00] font-medium border border-[#BBFF00]/30">
                    {currentPlan.name}
                  </span>
                </div>
              </div>
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsEditing(!isEditing)}
              className="text-slate-400 hover:text-[#BBFF00] hover:bg-[#BBFF00]/10"
              data-testid="edit-profile-btn"
            >
              {isEditing ? <X className="w-5 h-5" /> : <Edit2 className="w-5 h-5" />}
            </Button>
          </div>
          
          {/* Profile Form */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-slate-300">Nombre</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  disabled={!isEditing}
                  className="bg-[#131B2A] border-white/10 text-slate-100 disabled:text-slate-400"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-slate-300">Apellido</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  disabled={!isEditing}
                  className="bg-[#131B2A] border-white/10 text-slate-100 disabled:text-slate-400"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!isEditing}
                  className="pl-10 bg-[#131B2A] border-white/10 text-slate-100 disabled:text-slate-400"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="phone" className="text-slate-300">Teléfono</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleOpenPhoneDialog}
                  className="text-xs text-[#BBFF00] hover:text-[#A2DC00] hover:bg-[#BBFF00]/10 h-auto py-1"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Cambiar
                </Button>
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  disabled={true}
                  className="pl-10 bg-[#131B2A] border-white/10 text-slate-100 disabled:text-slate-400"
                  placeholder="Sin número registrado"
                />
                {user?.phone_verified && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Check className="w-4 h-4 text-[#BBFF00]" />
                  </div>
                )}
              </div>
              {user?.phone && !user?.phone_verified && (
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Número no verificado
                </p>
              )}
            </div>

            <div className="rounded-lg border border-white/10 bg-[#131B2A] p-3" data-testid="profile-email-verification-section">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  Estado del correo: {user?.email_verified ? <span className="text-[#BBFF00]">Verificado ✓</span> : <span className="text-amber-400">No verificado</span>}
                </p>
              </div>

              {/* Email verification flow */}
              {!user?.email_verified && (
                <div className="space-y-3">
                  {/* Benefits info box */}
                  <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <p className="text-xs text-amber-200 font-medium mb-1">
                      🎬 ¿Por qué verificar tu correo?
                    </p>
                    <ul className="text-xs text-slate-400 space-y-0.5">
                      <li>• <span className="text-amber-300">Sin verificar:</span> Tus videos se borran en <strong className="text-white">24 horas</strong></li>
                      <li>• <span className="text-[#BBFF00]">Verificado:</span> Tus videos se guardan <strong className="text-white">3 días (72h)</strong></li>
                    </ul>
                  </div>

                  {/* Email input + send button */}
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="tu@email.com"
                        className="pl-10 bg-[#0D121D] border-white/10 text-slate-100 placeholder:text-slate-500"
                        data-testid="profile-email-input"
                      />
                    </div>
                    <Button
                      onClick={handleSendEmailOTP}
                      disabled={emailSending || !formData.email?.trim() || !formData.email?.includes('@')}
                      className="bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-semibold px-4"
                      data-testid="profile-send-email-link-button"
                    >
                      {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar link'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    💡 Te enviaremos un link mágico. Solo haz clic para verificar.
                  </p>
                </div>
              )}

              {user?.email_verified && (
                <div className="p-2 rounded-lg bg-[#BBFF00]/10 border border-[#BBFF00]/20">
                  <p className="text-xs text-[#BBFF00]">
                    ✓ Tus replays se guardan 72 horas antes de ser eliminados automáticamente
                  </p>
                </div>
              )}
            </div>

            
            {isEditing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="pt-4"
              >
                <Button
                  className="w-full bg-[#BBFF00] hover:bg-[#A2DC00] text-[#070A0F] font-bold"
                  onClick={handleSave}
                  disabled={loading}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Guardar cambios
                </Button>
              </motion.div>
            )}
          </div>
        </motion.div>
        
        {/* Premium Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`rounded-2xl p-6 border ${
            user?.plan === 'premium' || user?.role === 'player_premium'
              ? 'bg-gradient-to-br from-yellow-500/20 via-orange-500/10 to-card border-yellow-500/30'
              : 'bg-gradient-to-br from-primary/10 via-card to-card border-primary/20'
          }`}
        >
          {user?.plan === 'premium' || user?.role === 'player_premium' ? (
            /* Premium Active */
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Crown className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg flex items-center gap-2">
                      Clipper Premium
                      <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full">Activo</span>
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Expira: {user?.premium_expires_at 
                        ? new Date(user.premium_expires_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-yellow-400">
                  <Check className="w-4 h-4" />
                  <span>Replays ilimitados</span>
                </div>
                <div className="flex items-center gap-2 text-yellow-400">
                  <Check className="w-4 h-4" />
                  <span>Sin marca de agua</span>
                </div>
                <div className="flex items-center gap-2 text-yellow-400">
                  <Check className="w-4 h-4" />
                  <span>Alta resolución</span>
                </div>
                <div className="flex items-center gap-2 text-yellow-400">
                  <Check className="w-4 h-4" />
                  <span>Soporte prioritario</span>
                </div>
              </div>
            </>
          ) : (
            /* Not Premium - Show upgrade option with Coming Soon */
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg">Clipper Premium</h3>
                    <p className="text-sm text-muted-foreground">Desbloquea todas las funciones</p>
                  </div>
                </div>
                <span className="px-3 py-1.5 bg-yellow-500/20 text-yellow-400 text-sm font-medium rounded-full">
                  Próximamente
                </span>
              </div>
              
              {/* Premium features */}
              <div className="space-y-2 text-sm">
                {PREMIUM_FEATURES.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-muted-foreground">
                    <span className="text-lg">{feature.icon}</span>
                    <div>
                      <span className="font-medium text-foreground">{feature.title}</span>
                      <span className="text-xs text-muted-foreground ml-2">- {feature.description}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Note about free features */}
              <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-sm text-green-400">
                  ⭐ <span className="font-medium">Por tiempo limitado:</span> Algunas características premium están disponibles en el plan gratuito.
                </p>
              </div>
            </>
          )}
        </motion.div>
        
        {/* 
         * =========================================================================
         * SECCIÓN OCULTA: Mis Botones Shelly
         * =========================================================================
         * Esta sección se usará en una fase futura del desarrollo cuando 
         * se habilite la funcionalidad de botones Shelly físicos para usuarios
         * tipo jugador. Por ahora está oculta pero el código está listo.
         * 
         * Para habilitar: quitar el comentario del bloque condicional
         * Funcionalidad implementada:
         * - Lista de botones asignados al usuario
         * - Solicitud de baja de botón
         * - Información de uso (1 click = 30s, 2 clicks = 60s @ 2x)
         * =========================================================================
         */}
        {/* {user?.role === 'club_owner' || user?.role === 'admin' ? ( */}
        {false && ( /* Temporalmente oculto para usuarios jugadores - habilitar cuando se active la funcionalidad Shelly */
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-card rounded-2xl border border-border overflow-hidden"
        >
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                  <Radio className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg">Mis Botones Shelly</h3>
                  <p className="text-sm text-muted-foreground">
                    {shellyButtons.length === 0 
                      ? 'No tienes botones asignados' 
                      : `${shellyButtons.length} botón${shellyButtons.length > 1 ? 'es' : ''} asignado${shellyButtons.length > 1 ? 's' : ''}`}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => navigate('/shop')}
              >
                <ShoppingCart className="w-4 h-4 mr-1" />
                Comprar
              </Button>
            </div>
          </div>
          
          {loadingButtons ? (
            <div className="p-8 text-center">
              <Radio className="w-8 h-8 mx-auto text-muted-foreground animate-pulse" />
              <p className="text-sm text-muted-foreground mt-2">Cargando botones...</p>
            </div>
          ) : shellyButtons.length === 0 ? (
            <div className="p-6 text-center">
              <Radio className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                No tienes botones Shelly asignados.
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Contacta al administrador para adquirir uno.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              <AnimatePresence>
                {shellyButtons.map((button) => (
                  <motion.div
                    key={button.device_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={`p-4 ${button.deactivation_requested ? 'bg-orange-500/5' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          button.deactivation_requested 
                            ? 'bg-orange-500/10' 
                            : 'bg-primary/10'
                        }`}>
                          <Radio className={`w-5 h-5 ${
                            button.deactivation_requested 
                              ? 'text-orange-500' 
                              : 'text-primary'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium">{button.device_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {button.club_name} • Cancha {button.court_number}
                          </p>
                          {button.deactivation_requested && (
                            <p className="text-xs text-orange-500 flex items-center gap-1 mt-1">
                              <AlertTriangle className="w-3 h-3" />
                              Baja solicitada - Pendiente de aprobación
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {!button.deactivation_requested && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => {
                            setSelectedButton(button);
                            setShowDeactivateDialog(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Dar de baja
                        </Button>
                      )}
                    </div>
                    
                    {/* Button info */}
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-blue-500/10 rounded-lg p-2 text-center">
                        <Zap className="w-4 h-4 mx-auto mb-1 text-blue-400" />
                        <p className="font-medium">1 Click</p>
                        <p className="text-muted-foreground">30s Normal</p>
                      </div>
                      <div className="bg-yellow-500/10 rounded-lg p-2 text-center">
                        <Zap className="w-4 h-4 mx-auto mb-1 text-yellow-400" />
                        <p className="font-medium">2 Clicks</p>
                        <p className="text-muted-foreground">60s @ 2x</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.div>
        )}
        
        {/* Mis Reservaciones Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.17 }}
          className="bg-card rounded-2xl border border-border overflow-hidden"
        >
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg">Mis Reservaciones</h3>
                  <p className="text-sm text-muted-foreground">
                    {loadingReservations 
                      ? 'Cargando...' 
                      : reservations.length === 0 
                        ? 'No tienes reservaciones activas' 
                        : `${reservations.length} reservación${reservations.length > 1 ? 'es' : ''}`}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => toast.info('Función disponible próximamente')}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Agendar
              </Button>
            </div>
          </div>
          
          {reservations.length > 0 ? (
            <div className="divide-y divide-border">
              {reservations.slice(0, 3).map((res, idx) => (
                <div key={res.id || idx} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{res.club_name || 'Club'} - {res.court_name || 'Cancha'}</p>
                      <p className="text-sm text-muted-foreground">
                        {res.date} • {res.start_time} - {res.end_time}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    res.payment_status === 'paid' || res.payment_status === 'confirmed'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {res.payment_status === 'paid' || res.payment_status === 'confirmed' ? 'Confirmada' : 'Pendiente'}
                  </span>
                </div>
              ))}
              {reservations.length > 3 && (
                <button
                  onClick={() => toast.info('Función disponible próximamente')}
                  className="w-full p-3 text-sm text-primary hover:bg-secondary/50 transition-colors text-center"
                >
                  Ver todas ({reservations.length})
                </button>
              )}
            </div>
          ) : (
            <div className="p-6 text-center">
              <p className="text-muted-foreground mb-4">¿Quieres jugar? Reserva una cancha en los clubes participantes</p>
              <Button
                onClick={() => toast.info('Función disponible próximamente')}
                variant="outline"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Ver clubes disponibles
              </Button>
            </div>
          )}
        </motion.div>
        
        {/* Seguridad Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.19 }}
          className="bg-card rounded-2xl border border-border divide-y divide-border"
        >
          <div className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-orange-500" />
              </div>
              <h3 className="font-display font-bold text-lg">Seguridad</h3>
            </div>
          </div>
          
          <button
            onClick={() => setShowPasswordDialog(true)}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-muted-foreground" />
              <div className="text-left">
                <p className="font-medium">Cambiar contraseña</p>
                <p className="text-sm text-muted-foreground">Actualiza tu contraseña de acceso</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
          
          <button
            onClick={handleOpenPhoneDialog}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-muted-foreground" />
              <div className="text-left">
                <p className="font-medium">Cambiar número de celular</p>
                <p className="text-sm text-muted-foreground">
                  {user?.phone ? `Actual: ${user.phone.slice(-4).padStart(user.phone.length, '*')}` : 'Sin número registrado'}
                </p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </motion.div>
        
        {/* Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-2xl border border-border divide-y divide-border"
        >
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Notificaciones</p>
                <p className="text-sm text-muted-foreground">Recibe alertas cuando tu replay esté listo</p>
              </div>
            </div>
            <Switch
              checked={settings.notifications}
              onCheckedChange={(checked) => setSettings({ ...settings, notifications: checked })}
            />
          </div>
          
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Descarga automática</p>
                <p className="text-sm text-muted-foreground">Descargar replays al estar listos</p>
              </div>
            </div>
            <Switch
              checked={settings.autoDownload}
              onCheckedChange={(checked) => setSettings({ ...settings, autoDownload: checked })}
            />
          </div>
        </motion.div>
        
        {/* FeedLoop */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.21 }}
          className="bg-card rounded-2xl border border-border overflow-hidden"
        >
          <button
            onClick={() => navigate('/feedloop')}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
            data-testid="feedloop-link"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <MessageSquarePlus className="w-5 h-5 text-yellow-500" />
              </div>
              <div className="text-left">
                <h3 className="font-display font-bold text-lg">FeedLoop</h3>
                <p className="text-sm text-muted-foreground">Fallas y Sugerencias</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </motion.div>
        
        {/* Ayuda / FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.22 }}
          className="bg-card rounded-2xl border border-border overflow-hidden"
        >
          <button
            onClick={() => setShowHelpSection(!showHelpSection)}
            className="w-full p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
                <HelpCircle className="w-5 h-5 text-purple-500" />
              </div>
              <div className="text-left">
                <h3 className="font-display font-bold text-lg">Ayuda y Preguntas Frecuentes</h3>
                <p className="text-sm text-muted-foreground">Resuelve tus dudas sobre Clipper</p>
              </div>
            </div>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showHelpSection ? 'rotate-180' : ''}`} />
          </button>
          
          <AnimatePresence>
            {showHelpSection && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-border"
              >
                {loadingFAQ ? (
                  <div className="p-8 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    {/* Category tabs */}
                    <div className="flex overflow-x-auto gap-2 p-4 border-b border-border">
                      <button
                        onClick={() => setSelectedCategory(null)}
                        className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                          !selectedCategory 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-secondary hover:bg-secondary/80 text-muted-foreground'
                        }`}
                      >
                        Todos
                      </button>
                      {faqCategories.map(cat => (
                        <button
                          key={cat.slug}
                          onClick={() => setSelectedCategory(cat.slug)}
                          className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors flex items-center gap-1 ${
                            selectedCategory === cat.slug
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-secondary hover:bg-secondary/80 text-muted-foreground'
                          }`}
                        >
                          <span>{cat.icon}</span>
                          {cat.name}
                        </button>
                      ))}
                    </div>
                    
                    {/* FAQ items */}
                    <div className="divide-y divide-border max-h-[400px] overflow-y-auto">
                      {(selectedCategory 
                        ? getFAQByCategory(selectedCategory) 
                        : faqItems
                      ).map((faq, idx) => (
                        <div key={faq.id} className="overflow-hidden">
                          <button
                            onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                            className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors text-left"
                          >
                            <p className="font-medium pr-4">{faq.question}</p>
                            <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${expandedFAQ === faq.id ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence>
                            {expandedFAQ === faq.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="px-4 pb-4"
                              >
                                <p className="text-sm text-muted-foreground bg-secondary/30 p-3 rounded-lg whitespace-pre-wrap">
                                  {faq.answer}
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      ))}
                      
                      {faqItems.length === 0 && (
                        <div className="p-8 text-center text-muted-foreground">
                          <HelpCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No hay preguntas frecuentes aún</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Mis preguntas enviadas */}
                    {myQuestions.length > 0 && (
                      <div className="border-t border-border">
                        <button
                          onClick={() => setShowMyQuestions(!showMyQuestions)}
                          className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-primary" />
                            <span className="font-medium">Mis preguntas enviadas ({myQuestions.length})</span>
                          </div>
                          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${showMyQuestions ? 'rotate-180' : ''}`} />
                        </button>
                        
                        <AnimatePresence>
                          {showMyQuestions && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="border-t border-border divide-y divide-border"
                            >
                              {myQuestions.map(q => (
                                <div key={q.id} className="p-4">
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="font-medium text-sm">{q.question}</p>
                                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                                      q.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                                      q.status === 'answered' ? 'bg-green-500/20 text-green-500' :
                                      'bg-blue-500/20 text-blue-500'
                                    }`}>
                                      {q.status === 'pending' ? 'Pendiente' : 
                                       q.status === 'answered' ? 'Respondida' : 
                                       q.status === 'published' ? 'Publicada' : q.status}
                                    </span>
                                  </div>
                                  {q.response && (
                                    <div className="mt-2 p-2 bg-secondary/30 rounded text-sm text-muted-foreground">
                                      <p className="text-xs text-primary mb-1">Respuesta:</p>
                                      {q.response}
                                    </div>
                                  )}
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {new Date(q.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </p>
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}
                    
                    {/* Submit question CTA */}
                    <div className="p-4 border-t border-border space-y-3">
                      <p className="text-sm text-muted-foreground text-center">
                        ¿No encontraste lo que buscabas?
                      </p>
                      <Button 
                        onClick={() => setShowQuestionDialog(true)}
                        className="w-full"
                        variant="outline"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Enviar mi pregunta
                      </Button>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        {/* Logout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            variant="outline"
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </Button>
        </motion.div>
        
        {/* Version */}
        <p className="text-center text-xs text-muted-foreground pb-4">
          Clipper v1.0.0 • Hecho en México 🇲🇽
        </p>
      </div>
      
      {/* Premium Plans Dialog */}
      <Dialog open={showPremiumDialog} onOpenChange={setShowPremiumDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              Elige tu plan
            </DialogTitle>
            <DialogDescription>
              Desbloquea más funciones con Clipper Premium
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid md:grid-cols-3 gap-4 mt-4">
            {PLANS.map((plan) => (
              <motion.div
                key={plan.id}
                whileHover={{ scale: plan.comingSoon ? 1 : 1.02 }}
                className={`relative rounded-xl p-5 border-2 transition-colors ${
                  plan.comingSoon 
                    ? 'border-border bg-muted/30 opacity-75 cursor-not-allowed'
                    : plan.popular 
                      ? 'border-primary bg-primary/5 cursor-pointer' 
                      : plan.current 
                        ? 'border-border bg-secondary/30 cursor-pointer'
                        : 'border-border hover:border-primary/50 cursor-pointer'
                }`}
                onClick={() => !plan.comingSoon && handleSelectPlan(plan)}
              >
                {plan.comingSoon && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-amber-500/90 text-white text-xs font-bold rounded-full">
                      PRÓXIMAMENTE
                    </span>
                  </div>
                )}
                {plan.popular && !plan.comingSoon && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-primary text-primary-foreground text-xs font-bold rounded-full">
                      POPULAR
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-4">
                  <h3 className="font-display font-bold text-lg">{plan.name}</h3>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">
                      {plan.price === 0 ? 'Gratis' : `$${plan.price}`}
                    </span>
                    {plan.period && (
                      <span className="text-muted-foreground">{plan.period}</span>
                    )}
                  </div>
                </div>
                
                <ul className="space-y-2 text-sm">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className={`w-4 h-4 ${plan.popular && !plan.comingSoon ? 'text-primary' : 'text-muted-foreground'}`} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Button
                  className={`w-full mt-4 ${plan.popular && !plan.comingSoon ? 'glow-primary' : ''}`}
                  variant={plan.current ? 'outline' : plan.comingSoon ? 'secondary' : plan.popular ? 'default' : 'secondary'}
                  disabled={plan.current || plan.comingSoon}
                >
                  {plan.current ? 'Plan actual' : plan.comingSoon ? 'Próximamente' : 'Seleccionar'}
                </Button>
              </motion.div>
            ))}
          </div>
          
          <p className="text-xs text-muted-foreground text-center mt-4">
            Los planes Pro y Equipo estarán disponibles pronto. ¡Mantente atento!
          </p>
        </DialogContent>
      </Dialog>
      
      {/* Shelly Deactivation Dialog */}
      <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="w-5 h-5" />
              Solicitar baja de botón
            </DialogTitle>
            <DialogDescription>
              Esta acción desactivará inmediatamente tu botón Shelly por seguridad.
              Un administrador revisará tu solicitud.
            </DialogDescription>
          </DialogHeader>
          
          {selectedButton && (
            <div className="space-y-4">
              <div className="bg-secondary/50 rounded-lg p-3">
                <p className="text-sm"><strong>Botón:</strong> {selectedButton.device_name}</p>
                <p className="text-sm"><strong>Club:</strong> {selectedButton.club_name}</p>
                <p className="text-sm"><strong>Cancha:</strong> {selectedButton.court_number}</p>
              </div>
              
              <div>
                <Label htmlFor="reason">Motivo de la baja</Label>
                <select
                  id="reason"
                  value={deactivationReason}
                  onChange={(e) => setDeactivationReason(e.target.value)}
                  className="w-full mt-1 p-2 rounded-md bg-secondary border border-border"
                >
                  <option value="Botón perdido">Botón perdido</option>
                  <option value="Botón robado">Botón robado</option>
                  <option value="Botón dañado">Botón dañado</option>
                  <option value="Ya no lo necesito">Ya no lo necesito</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                <p className="text-sm text-red-400">
                  <strong>Importante:</strong> Al solicitar la baja, tu botón dejará de funcionar 
                  inmediatamente por seguridad. No podrás revertir esta acción.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRequestDeactivation}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Solicitar baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Phone Change Dialog */}
      <Dialog open={showPhoneDialog} onOpenChange={(open) => {
        if (!open) {
          setPhoneStep(1);
          setPhoneCode('');
        }
        setShowPhoneDialog(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              {phoneStep === 1 ? 'Cambiar número de teléfono' : 'Verificar código'}
            </DialogTitle>
            <DialogDescription>
              {phoneStep === 1 
                ? 'Ingresa tu nuevo número de teléfono. Te enviaremos un código SMS para verificarlo.'
                : `Ingresa el código de 6 dígitos que enviamos a ${newPhone.slice(-4).padStart(newPhone.length, '*')}`
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {phoneStep === 1 ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="new_phone">Nuevo número de teléfono</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="new_phone"
                      type="tel"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      placeholder="+52 644 123 4567"
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Incluye el código de país (ej: +52 para México)
                  </p>
                </div>
                
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                  <p className="text-sm text-blue-400">
                    <strong>Nota:</strong> Te enviaremos un SMS con un código de verificación 
                    para confirmar que tienes acceso a este número.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="phone_code">Código de verificación</Label>
                  <Input
                    id="phone_code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    className="text-center text-2xl tracking-widest"
                    autoComplete="one-time-code"
                  />
                </div>
                
                <div className="flex justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPhoneStep(1);
                      setPhoneCode('');
                    }}
                    className="text-xs"
                  >
                    ← Cambiar número
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSendPhoneOTP}
                    disabled={phoneSending}
                    className="text-xs text-primary"
                  >
                    Reenviar código
                  </Button>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhoneDialog(false)}>
              Cancelar
            </Button>
            {phoneStep === 1 ? (
              <Button 
                onClick={handleSendPhoneOTP}
                disabled={phoneSending || !newPhone.trim()}
              >
                {phoneSending ? (
                  <>Enviando...</>
                ) : (
                  <>Enviar código SMS</>
                )}
              </Button>
            ) : (
              <Button 
                onClick={handleVerifyAndChangePhone}
                disabled={phoneVerifying || phoneCode.length < 4}
              >
                {phoneVerifying ? (
                  <>Verificando...</>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Verificar y guardar
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-500" />
              Cambiar contraseña
            </DialogTitle>
            <DialogDescription>
              Ingresa tu contraseña actual y la nueva contraseña.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePasswordChange} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current_password">Contraseña actual</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                placeholder="••••••••"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password">Nueva contraseña</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new}
                onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                placeholder="••••••••"
                minLength={8}
                required
              />
              <p className="text-xs text-muted-foreground">Mínimo 8 caracteres</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirmar nueva contraseña</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                placeholder="••••••••"
                required
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setShowPasswordDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={passwordChanging}>
                {passwordChanging ? 'Guardando...' : 'Cambiar contraseña'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Submit Question Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              Enviar una pregunta
            </DialogTitle>
            <DialogDescription>
              Envía tu pregunta y nuestro equipo te responderá lo antes posible.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="question_category">Categoría</Label>
              <select
                id="question_category"
                value={questionCategory}
                onChange={(e) => setQuestionCategory(e.target.value)}
                className="w-full p-2 rounded-md bg-secondary border border-border text-sm"
              >
                {faqCategories.map(cat => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
                <option value="otros">❓ Otros</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="question_text">Tu pregunta</Label>
              <textarea
                id="question_text"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                placeholder="Escribe tu pregunta aquí..."
                rows={4}
                className="w-full p-3 rounded-md bg-secondary border border-border text-sm resize-none"
              />
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowQuestionDialog(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmitQuestion}
              disabled={submittingQuestion || !newQuestion.trim()}
            >
              {submittingQuestion ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Enviar pregunta
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
